/**
 * Landing Footer — GenesisBox
 *
 * Multi-column footer mirroring the Genesis Frameworks company-site footer
 * (brand blurb · Product · ecosystem Links · Contact · copyright + disclaimer),
 * rendered in the live GenesisBox neon-glass theme.
 */

import { Box, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
  to?: string;
}

const PRODUCT_LINKS: FooterLink[] = [
  { label: 'Sign in', href: '', to: '/login' },
  { label: 'What it does', href: '#features' },
  { label: 'Viewer modes', href: '#viewer' },
  { label: 'Sharing & embeds', href: '#sharing' },
];

const ECOSYSTEM_LINKS: FooterLink[] = [
  { label: 'Genesis Frameworks', href: 'https://www.genesisframeworks.com/', external: true },
  { label: 'Genesis Chat', href: 'https://genesischat.online/', external: true },
  { label: 'AI Studio', href: 'https://aistudio.genesisframeworks.com/', external: true },
  { label: 'Image Tools', href: 'https://editor.genesisframeworks.com/converter.html', external: true },
];

function ColumnHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      className="font-label"
      sx={{ fontSize: 10, color: 'text.secondary', mb: 1.5 }}
    >
      {children}
    </Typography>
  );
}

const linkSx = {
  fontSize: 13,
  color: 'text.secondary',
  textDecoration: 'none',
  transition: 'color 160ms ease',
  '&:hover': { color: '#8ff5ff' },
} as const;

function FooterAnchor({ link }: { link: FooterLink }) {
  if (link.to) {
    return (
      <Typography component={RouterLink} to={link.to} sx={linkSx}>
        {link.label}
      </Typography>
    );
  }
  return (
    <Typography
      component="a"
      href={link.href}
      {...(link.external ? { target: '_blank', rel: 'noreferrer' } : {})}
      sx={linkSx}
    >
      {link.label}
    </Typography>
  );
}

export function LandingFooter() {
  return (
    <Box
      component="footer"
      sx={{
        borderTop: '1px solid rgba(143,245,255,0.08)',
        mt: { xs: 8, md: 12 },
        background: 'rgba(11, 11, 13, 0.45)',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.6fr 1fr 1fr 1fr' },
            gap: { xs: 4, sm: 5 },
          }}
        >
          {/* Brand blurb */}
          <Box sx={{ maxWidth: 320 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
              <Box
                component="img"
                src="/genesis-frameworks-logo.png?v=1"
                alt="Genesis Frameworks"
                sx={{
                  width: { xs: 60, sm: 72 },
                  height: { xs: 60, sm: 72 },
                  minWidth: 60,
                  minHeight: 60,
                  objectFit: 'contain',
                }}
              />
              <Typography sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: 16 }}>
                GENESIS FRAMEWORKS
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.7 }}>
              A shared cloud file browser with an embedded gallery, library, and PDF reader.
              Share a folder with clients or an audience without bouncing between tools. Part of
              the Genesis Frameworks family.
            </Typography>
          </Box>

          {/* Product */}
          <Box>
            <ColumnHeading>Product</ColumnHeading>
            <Stack spacing={1}>
              {PRODUCT_LINKS.map((link) => (
                <FooterAnchor key={link.label} link={link} />
              ))}
            </Stack>
          </Box>

          {/* Ecosystem */}
          <Box>
            <ColumnHeading>Genesis Frameworks</ColumnHeading>
            <Stack spacing={1}>
              {ECOSYSTEM_LINKS.map((link) => (
                <FooterAnchor key={link.label} link={link} />
              ))}
            </Stack>
          </Box>

          {/* Contact */}
          <Box>
            <ColumnHeading>Contact</ColumnHeading>
            <Stack spacing={1}>
              <Typography component="a" href="mailto:jong@genesisframeworks.com" sx={linkSx}>
                jong@genesisframeworks.com
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            mt: { xs: 5, md: 7 },
            pt: 3,
            borderTop: '1px solid rgba(143,245,255,0.06)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
            © 2026 Genesis Frameworks. All rights reserved.
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', maxWidth: 520, lineHeight: 1.6 }}>
            Access is granted per organization by an administrator. Shared folders open in their
            own viewer; review each share for its own access details.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
